import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CheckCircle2, User, Sparkles, SendIcon } from "lucide-react";
import type { BDRSummary } from "@shared/types";

interface Props {
  bdrs: BDRSummary[];
  onConfirm: (bdr: BDRSummary) => void;
}

// ─── Floating gradient blobs ──────────────────────────────────────────────
function GradientBlobs() {
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full mix-blend-normal filter blur-[128px] animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full mix-blend-normal filter blur-[128px] animate-pulse" style={{ animationDelay: "700ms" }} />
      <div className="absolute top-1/4 right-1/3 w-64 h-64 bg-fuchsia-500/10 rounded-full mix-blend-normal filter blur-[96px] animate-pulse" style={{ animationDelay: "1000ms" }} />
    </div>
  );
}

// ─── Custom dropdown option ───────────────────────────────────────────────
function BdrOption({
  bdr,
  isActive,
  onClick,
  index,
}: {
  bdr: BDRSummary;
  isActive: boolean;
  onClick: () => void;
  index: number;
}) {
  const initials = bdr.full_name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);

  return (
    <motion.div
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
        isActive
          ? "bg-white/10 text-white"
          : "text-white/70 hover:bg-white/5"
      }`}
      onClick={onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.04 }}
    >
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7573F7] to-[#C3A0F2] flex items-center justify-center text-white text-xs font-bold shrink-0 overflow-hidden">
        {bdr.photo_url ? (
          <img src={bdr.photo_url} alt={bdr.full_name} className="w-full h-full object-cover" />
        ) : (
          initials
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{bdr.full_name}</div>
        <div className="text-white/40 text-xs truncate">{bdr.email}</div>
      </div>
      {isActive && (
        <CheckCircle2 className="w-4 h-4 text-[#05FAFC] shrink-0" />
      )}
    </motion.div>
  );
}

// ─── Main Picker ──────────────────────────────────────────────────────────
export function BDRPicker({ bdrs, onConfirm }: Props) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = bdrs.find((b) => b.id === selectedId);

  // Mouse tracking for subtle glow
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (bdr: BDRSummary) => {
      setSelectedId(bdr.id);
      setDropdownOpen(false);
    },
    []
  );

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col w-full items-center justify-center text-white py-10 px-4 overflow-hidden"
    >
      {/* Background blobs */}
      <GradientBlobs />

      <div className="w-full max-w-xl mx-auto relative">
        <motion.div
          className="relative z-10 space-y-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Header */}
          <div className="text-center space-y-3">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block"
            >
              <h1 className="text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white/90 to-white/40 pb-1">
                Who's submitting?
              </h1>
              <motion.div
                className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "100%", opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
            </motion.div>
            <motion.p
              className="text-sm text-white/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Select your name to register a Discovery meeting
            </motion.p>
          </div>

          {/* Glassmorphic card */}
          <motion.div
            className="relative backdrop-blur-2xl bg-white/[0.02] rounded-2xl border border-white/[0.05] shadow-2xl"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            {/* Custom dropdown */}
            <div className="p-5" ref={dropdownRef}>
              <label className="block text-xs font-bold text-white/30 uppercase tracking-widest mb-3 px-1">
                Your name
              </label>

              {/* Select trigger */}
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 ${
                  dropdownOpen
                    ? "bg-white/[0.06] border border-white/[0.12]"
                    : "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.08]"
                }`}
              >
                {selected ? (
                  <>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7573F7] to-[#C3A0F2] flex items-center justify-center text-white text-xs font-bold shrink-0 overflow-hidden">
                      {selected.photo_url ? (
                        <img src={selected.photo_url} alt={selected.full_name} className="w-full h-full object-cover" />
                      ) : (
                        selected.full_name
                          .split(" ")
                          .map((w) => w[0])
                          .join("")
                          .slice(0, 2)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white/90 text-sm font-medium">
                        {selected.full_name}
                      </div>
                      <div className="text-white/40 text-xs truncate">
                        {selected.email}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-white/30" />
                    </div>
                    <span className="text-white/20 text-sm">
                      Select your name…
                    </span>
                  </>
                )}
                <ChevronDown
                  className={`w-4 h-4 text-white/30 shrink-0 transition-transform duration-200 ml-auto ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown menu — inline (not absolute) so it doesn't overlap */}
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    className="mt-2 backdrop-blur-xl bg-[#0A0118]/95 rounded-xl shadow-2xl border border-white/10 overflow-hidden"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="py-1 max-h-64 overflow-y-auto">
                      {bdrs.map((bdr, index) => (
                        <BdrOption
                          key={bdr.id}
                          bdr={bdr}
                          isActive={bdr.id === selectedId}
                          onClick={() => handleSelect(bdr)}
                          index={index}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick-select BDR avatars */}
            <div className="px-5 pt-4 pb-2 border-t border-white/[0.05]">
              <div className="text-[10px] font-bold text-white/25 uppercase tracking-widest mb-3 px-1">Quick select</div>
              <div className="flex items-center justify-center gap-3">
                {bdrs.map((bdr, index) => {
                  const initials = bdr.full_name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2);
                  const isSelected = bdr.id === selectedId;

                  return (
                    <motion.button
                      key={bdr.id}
                      onClick={() => {
                        setSelectedId(bdr.id);
                        setDropdownOpen(false);
                      }}
                      className={`flex flex-col items-center gap-2 px-4 py-3 rounded-2xl text-sm transition-all relative group ${
                        isSelected
                          ? "bg-white/[0.08] text-white/90 border border-white/[0.12]"
                          : "bg-white/[0.02] hover:bg-white/[0.06] text-white/50 hover:text-white/80 border border-transparent"
                      }`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.08 }}
                    >
                      <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-[#7573F7] to-[#C3A0F2] flex items-center justify-center text-white text-xl font-bold overflow-hidden ring-2 transition-all ${
                        isSelected ? "ring-[#05FAFC]/50 shadow-lg shadow-[#05FAFC]/20" : "ring-transparent"
                      }`}>
                        {bdr.photo_url ? (
                          <img src={bdr.photo_url} alt={bdr.full_name} className="w-full h-full object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                      <span className="font-medium text-sm">{bdr.full_name.split(" ")[0]}</span>
                      {isSelected && (
                        <motion.div
                          className="absolute inset-0 border border-[#7573F7]/30 rounded-2xl pointer-events-none"
                          layoutId="bdr-highlight"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Divider + continue button */}
            <div className="p-5 border-t border-white/[0.05] flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-white/30 text-xs">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Register · Discovery · Meetings</span>
              </div>

              <motion.button
                type="button"
                onClick={() => selected && onConfirm(selected)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                disabled={!selected}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
                  selected
                    ? "bg-white text-[#0A0118] shadow-lg shadow-white/10"
                    : "bg-white/[0.05] text-white/30 cursor-not-allowed"
                }`}
              >
                <SendIcon className="w-4 h-4" />
                <span>Continue</span>
              </motion.button>
            </div>
          </motion.div>

          {bdrs.length === 0 && (
            <motion.p
              className="text-sm text-red-400/80 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              No active BDRs found. Contact your vendor manager.
            </motion.p>
          )}
        </motion.div>
      </div>

      {/* Mouse-follow glow */}
      {isFocused && (
        <motion.div
          className="fixed w-[50rem] h-[50rem] rounded-full pointer-events-none z-0 opacity-[0.02] bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 blur-[96px]"
          animate={{
            x: mousePosition.x - 400,
            y: mousePosition.y - 400,
          }}
          transition={{
            type: "spring",
            damping: 25,
            stiffness: 150,
            mass: 0.5,
          }}
        />
      )}
    </div>
  );
}
